import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import { FaComments, FaTimes, FaPaperPlane, FaThumbsUp, FaThumbsDown, FaHeart, FaRocket, FaHandPaper, FaTrash, FaMapMarkerAlt, FaLaughSquint, FaExclamation, FaBullhorn, FaExclamationTriangle, FaChevronDown, FaDesktop, FaSpinner, FaCopy } from 'react-icons/fa';
import { ChatRoom } from 'amazon-ivs-chat-messaging';
import API from '../../API';
import logger from '../../utils/logger';
import intersectStaffIcon from '../images/intersect-staff-blue-icon.png';
import { releaseUnityCapture, releaseUnityCursorCapture, useFocus, useOverlay } from '../../contexts/FocusContext';
import { linkifyChatMessage } from '../../utils/linkifyChatMessage';
import { handleChatInputKeyDown } from './chatInputKeyboard';
import './UnityGame.css';

type ChatMessageType = 'default' | 'announcement' | 'system';

interface ChatMessage {
  id: string;
  userId: string;
  username: string;
  message: string;
  timestamp: Date;
  isLocal: boolean;
  isIntersectStaff?: boolean;
  chatType?: ChatMessageType;
}

type ReactionType = 'thumbs_up' | 'thumbs_down' | 'heart' | 'celebrate' | 'wave' | 'laugh' | 'exclamation';

interface MessageReaction {
  type: ReactionType;
  userId: string;
  username: string;
}

interface MessageReactions {
  [messageId: string]: {
    thumbs_up: MessageReaction[];
    thumbs_down: MessageReaction[];
    heart: MessageReaction[];
    celebrate: MessageReaction[];
    wave: MessageReaction[];
    laugh: MessageReaction[];
    exclamation: MessageReaction[];
  };
}

/** Intersect-style user id (UUID) — when the visible label matches this, resolve username via API. */
const INTERSECT_USER_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

type ChatPanelSize = { width: number; height: number };

const CHAT_PANEL_SIZE_KEY = 'ivs-chat-panel-size';
const CHAT_PANEL_DEFAULT_SIZE: ChatPanelSize = { width: 360, height: 320 };
const CHAT_PANEL_MIN_SIZE: ChatPanelSize = { width: 300, height: 260 };
const CHAT_PANEL_MAX_SIZE: ChatPanelSize = { width: 720, height: 720 };

function clampChatPanelSize(size: ChatPanelSize): ChatPanelSize {
  const viewportWidth = typeof window === 'undefined' ? CHAT_PANEL_MAX_SIZE.width : window.innerWidth;
  const viewportHeight = typeof window === 'undefined' ? CHAT_PANEL_MAX_SIZE.height : window.innerHeight;
  const maxWidth = Math.max(CHAT_PANEL_MIN_SIZE.width, Math.min(CHAT_PANEL_MAX_SIZE.width, viewportWidth - 32));
  const maxHeight = Math.max(CHAT_PANEL_MIN_SIZE.height, Math.min(CHAT_PANEL_MAX_SIZE.height, viewportHeight - 32));
  return {
    width: Math.min(maxWidth, Math.max(CHAT_PANEL_MIN_SIZE.width, Math.round(size.width))),
    height: Math.min(maxHeight, Math.max(CHAT_PANEL_MIN_SIZE.height, Math.round(size.height))),
  };
}

function getSavedChatPanelSize(): ChatPanelSize {
  try {
    const saved = localStorage.getItem(CHAT_PANEL_SIZE_KEY);
    if (!saved) return CHAT_PANEL_DEFAULT_SIZE;
    const parsed = JSON.parse(saved);
    if (typeof parsed?.width !== 'number' || typeof parsed?.height !== 'number') return CHAT_PANEL_DEFAULT_SIZE;
    return clampChatPanelSize(parsed);
  } catch {
    return CHAT_PANEL_DEFAULT_SIZE;
  }
}

function ivsAttrStr(v: unknown): string {
  if (v == null) return '';
  const s = String(v).trim();
  return s;
}

/**
 * Display name from IVS payload. IVS Chat UserId is the intersect id; attributes may be missing or
 * only present on some clients, and casing varies — avoid using Attributes.userId as a display name.
 */
function senderDisplayLabelFromIvsMessage(message: any, messageUserId: string): string {
  if (!message) return ivsAttrStr(messageUserId) || 'Unknown';
  const name =
    ivsAttrStr(message.Sender?.Attributes?.displayName) ||
    ivsAttrStr(message.sender?.attributes?.displayName) ||
    ivsAttrStr(message.Attributes?.displayName) ||
    ivsAttrStr(message.attributes?.displayName) ||
    ivsAttrStr(message.Sender?.Attributes?.DisplayName) ||
    ivsAttrStr(message.sender?.attributes?.DisplayName) ||
    ivsAttrStr(message.Sender?.Attributes?.username) ||
    ivsAttrStr(message.sender?.attributes?.username) ||
    ivsAttrStr(message.Attributes?.username) ||
    ivsAttrStr(message.attributes?.username);
  if (name) return name;
  return ivsAttrStr(messageUserId) || 'Unknown';
}

export interface HandRaiseSignal {
  userId: string;
  displayName: string;
  raised: boolean;
  at: number;
}

export interface ClapSignal {
  userId: string;
  displayName: string;
  at: number;
}

interface IVSRealtimeChatProps {
  roomArn?: string;
  messagingEndpoint?: string;
  accessToken?: string;
  userId?: string;
  roomName?: string;
  roomId?: string;
  eventId?: string;
  podiumPosition?: string;
  debugIsIntersectStaff?: boolean; // For sandbox testing - overrides API response
  onPodiumTeleport?: () => void; // Callback when user teleports to podium
  onScreenSharePermissionGranted?: () => void; // Callback when current user is granted screen share permission
  onScreenSharePermissionRevoked?: () => void; // Callback when current user's screen share permission is revoked
  /** Raise/lower hand for any participant; local user is notified from sendHandRaiseState after send (IVS often does not echo to sender). */
  onHandRaiseSignal?: (e: HandRaiseSignal) => void;
  /** Breakout room clap; local user is notified from sendBreakoutClap after send (IVS often does not echo to sender). */
  onClapSignal?: (e: ClapSignal) => void;
  /** Human-readable name sent with raise-hand (e.g. from IVS People list); avoids showing raw user id in toasts */
  handRaiseDisplayName?: string;
  // Tour/onboarding identifier
  dataTourId?: string;
}

export interface IVSRealtimeChatRef {
  focusInput: () => void;
  isIntersectStaff: () => boolean;
  scrollToBottom: () => void;
  sendHandRaiseState: (raised: boolean) => Promise<void>;
  /** One-shot breakout applause signal (silent chat message). */
  sendBreakoutClap: () => Promise<void>;
  /** Send MOD_TELEPORT via IVS for the current room podium (`targetUserId` = intersect id and/or IVS chat username, same as People / chat). */
  sendModeratorTeleportToUser: (targetUserId: string) => Promise<void>;
  /** Same IVS path as chat moderator “Grant screen share” (`targetUserId` = intersect / IVS user id). */
  sendModeratorGrantScreenShare: (targetUserId: string) => Promise<void>;
  /** Same IVS path as chat moderator “Revoke screen share”. */
  sendModeratorRevokeScreenShare: (targetUserId: string) => Promise<void>;
  /** Staff: broadcast lower-hand for `targetUserId` so the raised queue updates for everyone. */
  sendModeratorUnraiseHand: (targetUserId: string) => Promise<void>;
}

const IVSRealtimeChat = forwardRef<IVSRealtimeChatRef, IVSRealtimeChatProps>(({ 
  roomArn,
  messagingEndpoint,
  accessToken,
  userId,
  roomName,
  roomId,
  eventId,
  podiumPosition,
  debugIsIntersectStaff,
  onPodiumTeleport,
  onScreenSharePermissionGranted,
  onScreenSharePermissionRevoked,
  onHandRaiseSignal,
  onClapSignal,
  handRaiseDisplayName,
  dataTourId
}, ref) => {
  const [isOpen, setIsOpen] = useState(() => {
    // Persist chat open/closed state across room switches
    const saved = localStorage.getItem('ivs-chat-open');
    return saved !== null ? saved === 'true' : true; // Default to open if no saved state
  });
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [chatPanelSize, setChatPanelSize] = useState<ChatPanelSize>(getSavedChatPanelSize);
  const isOpenRef = useRef(isOpen); // Ref to track isOpen in callbacks
  const countedMessageIds = useRef<Set<string>>(new Set()); // Track which messages we've already counted
  
  // Keep ref in sync with state
  useEffect(() => {
    isOpenRef.current = isOpen;
    // Clear unread count when chat is opened
    if (isOpen) {
      setUnreadCount(0);
      countedMessageIds.current.clear(); // Clear tracked IDs when opening chat
    }
  }, [isOpen]);
  useEffect(() => {
    if (isOpen && pendingInputFocusRef.current && inputRef.current) {
      pendingInputFocusRef.current = false;
      releaseUnityCapture();
      inputRef.current.focus();
    }
  }, [isOpen, roomArn, messagingEndpoint]);
  const [inputMessage, setInputMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [localUsername, setLocalUsername] = useState<string>(userId || 'Unknown');
  const [localIsIntersectStaffFromAPI, setLocalIsIntersectStaffFromAPI] = useState(false);
  const [reactions, setReactions] = useState<MessageReactions>({});
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [deleteConfirmMessageId, setDeleteConfirmMessageId] = useState<string | null>(null);
  const [isDeletingMessage, setIsDeletingMessage] = useState(false);
  const [teleportConfirmMessage, setTeleportConfirmMessage] = useState<ChatMessage | null>(null);
  const [screenShareConfirmMessage, setScreenShareConfirmMessage] = useState<ChatMessage | null>(null);
  const [screenShareRevokeConfirmMessage, setScreenShareRevokeConfirmMessage] = useState<ChatMessage | null>(null);
  const [selectedChatType, setSelectedChatType] = useState<ChatMessageType>('default');
  const [showChatTypeDropdown, setShowChatTypeDropdown] = useState(false);
  const [messageTimestamps, setMessageTimestamps] = useState<Record<string, number>>({});

  // Use debug override if provided (sandbox), otherwise use API response
  const localIsIntersectStaff = debugIsIntersectStaff !== undefined ? debugIsIntersectStaff : localIsIntersectStaffFromAPI;

  // Debug logging for podium position
  useEffect(() => {
    logger.log('[IVS Chat] Podium position updated:', {
      podiumPosition,
      hasPodium: !!podiumPosition,
      isStaff: localIsIntersectStaff,
      isDebugOverride: debugIsIntersectStaff !== undefined
    });
  }, [podiumPosition, localIsIntersectStaff, debugIsIntersectStaff]);
  
  const onHandRaiseSignalRef = useRef(onHandRaiseSignal);
  onHandRaiseSignalRef.current = onHandRaiseSignal;

  const onClapSignalRef = useRef(onClapSignal);
  onClapSignalRef.current = onClapSignal;

  /** IVS Chat `Sender.UserId` from CreateChatToken — Intersect username, not the participant-token UUID. Used to match MOD_* commands. */
  const ivsChatUserIdRef = useRef<string | null>(null);

  // Prefer app-provided display name (IVS People / sandbox) when it is not just the raw user id
  useEffect(() => {
    const n = handRaiseDisplayName?.trim();
    if (n && userId && n !== userId) setLocalUsername(n);
  }, [handRaiseDisplayName, userId]);

  // Room changes (e.g. breakout): userId can arrive after mount while state was initialized as 'Unknown'
  useEffect(() => {
    if (!userId) return;
    setLocalUsername((prev) => (prev === 'Unknown' ? userId : prev));
  }, [userId]);

  const chatRoomRef = useRef<ChatRoom | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  /** Defer clearing input focus so context menu → Copy can finish before Unity re-captures the keyboard. */
  const inputBlurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resizeCleanupRef = useRef<(() => void) | null>(null);
  const pendingInputFocusRef = useRef(false);
  const { registerOverlay, unregisterOverlay } = useFocus();
  const isInitialLoadRef = useRef(true); // Track if this is initial message load
  
  // Typing state for Unity events (debounced)
  const isTypingRef = useRef(false);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const TYPING_DEBOUNCE_MS = 1500; // Send "stopped typing" after 1.5s of no input

  const parsePodiumPosition = useCallback((positionStr: string): { x: number; y: number; z: number } | null => {
    try {
      const cleaned = positionStr.replace(/[()]/g, '').trim();
      const parts = cleaned.split(',').map((p) => parseFloat(p.trim()));

      if (parts.length === 3 && parts.every((p) => !isNaN(p))) {
        return { x: parts[0], y: parts[1], z: parts[2] };
      }
      return null;
    } catch (error) {
      console.error('Error parsing podium position:', error);
      return null;
    }
  }, []);

  const executeModeratorTeleport = useCallback(
    async (targetUserId: string) => {
      if (!localIsIntersectStaff || !podiumPosition) {
        throw new Error('Moderator teleport unavailable');
      }
      const room = chatRoomRef.current;
      if (!room) {
        throw new Error('Chat not connected');
      }
      const position = parsePodiumPosition(podiumPosition);
      if (!position) {
        throw new Error('Invalid podium position');
      }
      const chatId = ivsChatUserIdRef.current?.trim() || '';
      const isTargetingSelf =
        targetUserId === userId ||
        targetUserId === localUsername ||
        (chatId !== '' && targetUserId === chatId);

      logger.log('[IVS Chat] Sending moderator teleport command:', {
        targetUser: targetUserId,
        position,
        isTargetingSelf,
      });

      const teleportCommand = {
        type: 'MOD_TELEPORT',
        targetUserId,
        position,
        rotation: { x: 0, y: 0, z: 0 },
      };

      const requestId = `teleport-${Date.now()}-${Math.random()}`;
      await room.sendMessage({
        id: requestId,
        content: JSON.stringify(teleportCommand),
        action: 'SEND_MESSAGE',
        requestId,
        attributes: {
          messageType: 'MOD_TELEPORT',
          targetUserId: String(targetUserId),
        },
      } as any);

      logger.log('[IVS Chat] Moderator teleport command sent successfully');

      if (isTargetingSelf && window.sendMessageToUnity) {
        logger.log('[IVS Chat] Teleporting self - executing immediately');
        const teleportPayload = {
          eventType: 'Teleport',
          payload: JSON.stringify({
            position,
            rotation: { x: 0, y: 0, z: 0 },
          }),
        };
        window.sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(teleportPayload));

        if (onPodiumTeleport) {
          onPodiumTeleport();
        }
      }
    },
    [localIsIntersectStaff, podiumPosition, userId, localUsername, onPodiumTeleport, parsePodiumPosition]
  );

  const executeModeratorGrantScreenShare = useCallback(
    async (targetUserId: string) => {
      if (!localIsIntersectStaff) {
        throw new Error('Only moderators can grant screen share');
      }
      const room = chatRoomRef.current;
      if (!room) {
        throw new Error('Chat not connected');
      }
      const permissionResponse = await API.setScreenSharePermission(accessToken, {
        eventId,
        userId: targetUserId,
        enabled: true,
      });
      if (!permissionResponse.ok) {
        throw new Error('Could not save screen share permission');
      }
      const chatIdGrant = ivsChatUserIdRef.current?.trim() || '';
      const isTargetingSelf =
        targetUserId === userId ||
        targetUserId === localUsername ||
        (chatIdGrant !== '' && targetUserId === chatIdGrant);

      logger.log('[IVS Chat] Sending screen share permission grant:', {
        targetUser: targetUserId,
        isTargetingSelf,
      });

      const grantCommand = {
        type: 'MOD_GRANT_SCREENSHARE',
        targetUserId,
      };
      const requestId = `screenshare-grant-${Date.now()}-${Math.random()}`;
      await room.sendMessage({
        id: requestId,
        content: JSON.stringify(grantCommand),
        action: 'SEND_MESSAGE',
        requestId,
        attributes: {
          messageType: 'MOD_GRANT_SCREENSHARE',
          targetUserId,
        },
      } as any);

      logger.log('[IVS Chat] Screen share permission grant sent successfully');

      if (isTargetingSelf && onScreenSharePermissionGranted) {
        logger.log('[IVS Chat] Granting permission to self - updating immediately');
        onScreenSharePermissionGranted();
      }
    },
    [accessToken, eventId, localIsIntersectStaff, userId, localUsername, onScreenSharePermissionGranted]
  );

  const executeModeratorRevokeScreenShare = useCallback(
    async (targetUserId: string) => {
      if (!localIsIntersectStaff) {
        throw new Error('Only moderators can revoke screen share');
      }
      const room = chatRoomRef.current;
      if (!room) {
        throw new Error('Chat not connected');
      }
      const permissionResponse = await API.setScreenSharePermission(accessToken, {
        eventId,
        userId: targetUserId,
        enabled: false,
      });
      if (!permissionResponse.ok) {
        throw new Error('Could not save screen share permission');
      }
      const chatIdRevoke = ivsChatUserIdRef.current?.trim() || '';
      const isTargetingSelf =
        targetUserId === userId ||
        targetUserId === localUsername ||
        (chatIdRevoke !== '' && targetUserId === chatIdRevoke);

      logger.log('[IVS Chat] Sending screen share permission revoke:', {
        targetUser: targetUserId,
        isTargetingSelf,
      });

      const revokeCommand = {
        type: 'MOD_REVOKE_SCREENSHARE',
        targetUserId,
      };
      const requestId = `screenshare-revoke-${Date.now()}-${Math.random()}`;
      await room.sendMessage({
        id: requestId,
        content: JSON.stringify(revokeCommand),
        action: 'SEND_MESSAGE',
        requestId,
        attributes: {
          messageType: 'MOD_REVOKE_SCREENSHARE',
          targetUserId,
        },
      } as any);

      logger.log('[IVS Chat] Screen share permission revoke sent successfully');

      if (isTargetingSelf && onScreenSharePermissionRevoked) {
        logger.log('[IVS Chat] Revoking permission from self - updating immediately');
        onScreenSharePermissionRevoked();
      }
    },
    [accessToken, eventId, localIsIntersectStaff, userId, localUsername, onScreenSharePermissionRevoked]
  );

  const executeModeratorUnraiseHand = useCallback(
    async (targetUserId: string) => {
      if (!localIsIntersectStaff) {
        throw new Error('Only moderators can lower hands for others');
      }
      const room = chatRoomRef.current;
      if (!room) {
        throw new Error('Chat not connected');
      }
      const tid = String(targetUserId).trim();
      if (!tid) {
        throw new Error('Invalid target');
      }
      const chatId = ivsChatUserIdRef.current?.trim() || '';
      const isTargetingSelf =
        tid === userId ||
        tid === localUsername ||
        (chatId !== '' && tid === chatId);

      const cmd = { type: 'MOD_UNRAISE_HAND', targetUserId: tid };
      const requestId = `mod-unraise-hand-${Date.now()}-${Math.random()}`;
      await room.sendMessage({
        id: requestId,
        content: JSON.stringify(cmd),
        action: 'SEND_MESSAGE',
        requestId,
        attributes: {
          messageType: 'MOD_UNRAISE_HAND',
          targetUserId: tid,
        },
      } as any);

      logger.log('[IVS Chat] MOD_UNRAISE_HAND sent', { targetUserId: tid });

      if (isTargetingSelf) {
        onHandRaiseSignalRef.current?.({
          userId: tid,
          displayName: '',
          raised: false,
          at: Date.now(),
        });
      }
    },
    [localIsIntersectStaff, userId, localUsername]
  );

  // Only text entry needs exclusive keyboard capture, not open chat dialogs.
  // Preserve the input's delayed blur/context-menu handoff, not the entire chat panel.
  useOverlay('chat-input', isInputFocused, { modal: true });
  useOverlay('chat-confirmations', deleteConfirmMessageId !== null || teleportConfirmMessage !== null || screenShareConfirmMessage !== null || screenShareRevokeConfirmMessage !== null);

  useEffect(() => {
    return () => {
      if (inputBlurTimeoutRef.current) {
        clearTimeout(inputBlurTimeoutRef.current);
        inputBlurTimeoutRef.current = null;
      }
      resizeCleanupRef.current?.();
    };
  }, []);

  // Function to only release cursor (keep keyboard for movement when hovering chat)
  const releaseCursorOnly = () => {
    if (window.sendMessageToUnity) {
      try {
        // Disable cursor capture only - keyboard stays active
        const captureCursorData = {
          eventType: 'CaptureCursor',
          payload: JSON.stringify({ capture: false })
        };
        window.sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(captureCursorData));
        logger.log('[Chat→Unity] Released cursor only (keyboard still active)');
      } catch (error) {
        console.error('[Chat→Unity] Error releasing cursor:', error);
      }
    }
  };

  // Send typing status to Unity (debounced to avoid spam)
  const sendTypingStatusToUnity = (isTyping: boolean) => {
    if (window.sendMessageToUnity) {
      try {
        const typingData = {
          eventType: 'IsTyping',
          payload: JSON.stringify({ isTyping })
        };
        window.sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(typingData));
        logger.log('[Chat→Unity] Sent typing status:', isTyping);
      } catch (error) {
        console.error('[Chat→Unity] Error sending typing status:', error);
      }
    }
  };

  // Handle input change with debounced typing events
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputMessage(value);

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    if (value.length > 0) {
      // Only send "started typing" if we weren't already typing
      if (!isTypingRef.current) {
        isTypingRef.current = true;
        sendTypingStatusToUnity(true);
      }

      // Set timeout to send "stopped typing" after debounce period
      typingTimeoutRef.current = setTimeout(() => {
        isTypingRef.current = false;
        sendTypingStatusToUnity(false);
      }, TYPING_DEBOUNCE_MS);
    } else {
      // Input is empty, send "stopped typing" immediately
      if (isTypingRef.current) {
        isTypingRef.current = false;
        sendTypingStatusToUnity(false);
      }
    }
  };

  // Expose methods to parent component
  useImperativeHandle(
    ref,
    () => ({
      focusInput: () => {
        pendingInputFocusRef.current = true;
        setIsOpen(true);
        releaseUnityCapture();
        if (inputRef.current) {
          pendingInputFocusRef.current = false;
          inputRef.current.focus();
        }
      },
      isIntersectStaff: () => localIsIntersectStaff,
      scrollToBottom: () => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      },
      sendHandRaiseState: async (raised: boolean) => {
        const room = chatRoomRef.current;
        if (!room || !isConnected) {
          throw new Error('Chat not connected');
        }
        const requestId = `hand-${raised ? 'up' : 'down'}-${Date.now()}-${Math.random()}`;
        const fromProfile = handRaiseDisplayName?.trim();
        const fromLocal =
          localUsername && localUsername !== 'Unknown' ? localUsername.trim() : '';
        const fromId = typeof userId === 'string' ? userId.trim() : '';
        const displayName = fromProfile || fromLocal || fromId || 'Unknown';
        await room.sendMessage({
          id: requestId,
          content: JSON.stringify({
            v: 1,
            ts: Date.now(),
            displayName,
            username: displayName,
          }),
          action: 'SEND_MESSAGE',
          requestId,
          attributes: {
            messageType: raised ? 'RAISE_HAND' : 'LOWER_HAND',
            displayName,
            username: displayName,
          },
        } as any);

        const localUid =
          (typeof userId === 'string' && userId.trim()) ||
          (ivsChatUserIdRef.current && String(ivsChatUserIdRef.current).trim()) ||
          '';
        onHandRaiseSignalRef.current?.({
          userId: localUid || 'unknown',
          displayName,
          raised,
          at: Date.now(),
        });
      },
      sendBreakoutClap: async () => {
        const room = chatRoomRef.current;
        if (!room || !isConnected) {
          throw new Error('Chat not connected');
        }
        const requestId = `clap-${Date.now()}-${Math.random()}`;
        const fromProfile = handRaiseDisplayName?.trim();
        const fromLocal =
          localUsername && localUsername !== 'Unknown' ? localUsername.trim() : '';
        const fromId = typeof userId === 'string' ? userId.trim() : '';
        const displayName = fromProfile || fromLocal || fromId || 'Unknown';
        const ts = Date.now();
        await room.sendMessage({
          id: requestId,
          content: JSON.stringify({
            v: 1,
            ts,
            displayName,
            username: displayName,
          }),
          action: 'SEND_MESSAGE',
          requestId,
          attributes: {
            messageType: 'BREAKOUT_CLAP',
            displayName,
            username: displayName,
          },
        } as any);

        const localUid =
          (typeof userId === 'string' && userId.trim()) ||
          (ivsChatUserIdRef.current && String(ivsChatUserIdRef.current).trim()) ||
          '';
        onClapSignalRef.current?.({
          userId: localUid || 'unknown',
          displayName,
          at: ts,
        });
      },
      sendModeratorTeleportToUser: (targetUserId: string) => executeModeratorTeleport(targetUserId),
      sendModeratorGrantScreenShare: (targetUserId: string) => executeModeratorGrantScreenShare(targetUserId),
      sendModeratorRevokeScreenShare: (targetUserId: string) => executeModeratorRevokeScreenShare(targetUserId),
      sendModeratorUnraiseHand: (targetUserId: string) => executeModeratorUnraiseHand(targetUserId),
    }),
    [
      isConnected,
      handRaiseDisplayName,
      localUsername,
      userId,
      localIsIntersectStaff,
      executeModeratorTeleport,
      executeModeratorGrantScreenShare,
      executeModeratorRevokeScreenShare,
      executeModeratorUnraiseHand,
    ]
  );

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current && messages.length > 0) {
      if (isInitialLoadRef.current) {
        // For initial load (historical messages), use immediate scroll with a small delay
        // to ensure the DOM has fully rendered all messages
        const scrollTimeout = setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
          }
          isInitialLoadRef.current = false;
        }, 100);
        return () => clearTimeout(scrollTimeout);
      } else {
        // For new messages, use smooth scrolling
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [messages]);

  // Persist chat open/closed state to localStorage
  useEffect(() => {
    localStorage.setItem('ivs-chat-open', String(isOpen));
  }, [isOpen]);

  useEffect(() => {
    const clampToViewport = () => setChatPanelSize((current) => clampChatPanelSize(current));
    window.addEventListener('resize', clampToViewport);
    return () => window.removeEventListener('resize', clampToViewport);
  }, []);

  const beginChatResize = useCallback((event: React.PointerEvent, horizontalDirection: 1 | -1) => {
    if (window.matchMedia('(max-width: 640px)').matches) return;
    event.preventDefault();
    event.stopPropagation();
    resizeCleanupRef.current?.();
    const handle = event.currentTarget;
    const pointerId = event.pointerId;
    try { handle.setPointerCapture(pointerId); } catch { /* Window listeners remain as fallback. */ }
    registerOverlay('chat-resize');

    const startX = event.clientX;
    const startY = event.clientY;
    const startSize = chatPanelSize;
    let finalSize = startSize;

    const move = (pointerEvent: PointerEvent) => {
      if (pointerEvent.pointerId !== pointerId) return;
      finalSize = clampChatPanelSize({
        width: startSize.width + ((pointerEvent.clientX - startX) * horizontalDirection),
        height: startSize.height - (pointerEvent.clientY - startY),
      });
      setChatPanelSize(finalSize);
    };
    const finish = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
      window.removeEventListener('blur', finish);
      handle.removeEventListener('lostpointercapture', finish);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      try { if (handle.hasPointerCapture(pointerId)) handle.releasePointerCapture(pointerId); } catch { /* Already released. */ }
      resizeCleanupRef.current = null;
      unregisterOverlay('chat-resize');
      try { localStorage.setItem(CHAT_PANEL_SIZE_KEY, JSON.stringify(finalSize)); } catch { /* Keep the resized panel usable even when storage is unavailable. */ }
    };
    const onVisibilityChange = () => { if (document.hidden) finish(); };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
    window.addEventListener('blur', finish);
    handle.addEventListener('lostpointercapture', finish);
    document.addEventListener('visibilitychange', onVisibilityChange);
    resizeCleanupRef.current = finish;
  }, [chatPanelSize, registerOverlay, unregisterOverlay]);

  // Close chat type dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (showChatTypeDropdown) {
        setShowChatTypeDropdown(false);
      }
    };
    
    if (showChatTypeDropdown) {
      // Add a small delay to prevent immediate closing when opening
      const timer = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 100);
      
      return () => {
        clearTimeout(timer);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showChatTypeDropdown]);

  // Clear state when room changes
  useEffect(() => {
    // Clear messages and reactions when switching rooms
    setMessages([]);
    setReactions({});
    setIsConnected(false);
    // Reset initial load flag so next batch of messages scrolls to bottom
    isInitialLoadRef.current = true;
  }, [roomArn]);

  // Initialize chat room connection
  useEffect(() => {
    if (!roomArn || !messagingEndpoint || !accessToken) {
      return;
    }

    const unsubscribeFunctions: (() => void)[] = [];

    const connectToChatRoom = async () => {
      ivsChatUserIdRef.current = null;
      try {
        // Fetch chat token from API
        const fetchChatToken = async (): Promise<{ token: string; intersectStaff?: boolean }> => {
          try {
            // Use room-specific token if roomId is provided, otherwise fall back to event-level token
            const response = roomId && eventId
              ? await API.getIVSChatTokenForRoom(accessToken, {
                  eventId: eventId,
                  roomId: roomId
                })
              : await API.getIVSChatToken(accessToken, {
                  eventId: eventId || '22222'
            });

            if (response.status !== 200) {
              throw new Error(`Failed to fetch chat token: ${response.status}`);
            }

            const data = await response.json();
            
            // Handle case where data might be the token string directly
            let token: string;
            let intersectStaffValue: boolean | string | undefined;
            
            if (typeof data === 'string') {
              // If data is just the token string
              token = data;
              intersectStaffValue = undefined;
            } else {
              // If data is an object
              token = data.token || data.chatToken || (typeof data === 'string' ? data : '');
              intersectStaffValue = data.intersectStaff;
            }
            
            // Extract and set intersectStaff from response
            if (intersectStaffValue !== undefined) {
              let isIntersectStaff: boolean;
              if (typeof intersectStaffValue === 'boolean') {
                isIntersectStaff = intersectStaffValue;
              } else if (typeof intersectStaffValue === 'string') {
                isIntersectStaff = intersectStaffValue.toLowerCase() === 'true';
              } else {
                isIntersectStaff = Boolean(intersectStaffValue);
              }
              setLocalIsIntersectStaffFromAPI(isIntersectStaff);
            }
            
            // Convert intersectStaff to boolean for return type
            const intersectStaffBool = intersectStaffValue !== undefined 
              ? (typeof intersectStaffValue === 'boolean' 
                  ? intersectStaffValue 
                  : (typeof intersectStaffValue === 'string' 
                      ? intersectStaffValue.toLowerCase() === 'true' 
                      : Boolean(intersectStaffValue)))
              : undefined;
            
            return { token, intersectStaff: intersectStaffBool };
          } catch (error) {
            console.error('Error fetching chat token:', error);
            throw error;
          }
        };

        // Create token provider function
        const tokenProvider = async () => {
          const result = await fetchChatToken();
          return { token: result.token };
        };

        // Create ChatRoom instance
        const chatRoom = new ChatRoom({
          regionOrUrl: messagingEndpoint,
          tokenProvider: tokenProvider,
        });

        chatRoomRef.current = chatRoom;

        // Function to fetch chat history from backend
        const fetchChatHistory = async () => {
          if (!eventId || !roomId || !accessToken) return;
          
          try {
            const response = await API.getChatHistory(accessToken, { eventId, roomId, limit: 200 });
            if (response.status === 200) {
              const data = await response.json();
              
              // Convert API response to ChatMessage format (skip ephemeral hand signals if persisted)
              const rawHistory = Array.isArray(data.messages) ? data.messages : [];
              const historicalMessages: ChatMessage[] = rawHistory
                .filter((msg: any) => {
                  const mt = msg.messageType || msg.attributes?.messageType;
                  return (
                    mt !== 'RAISE_HAND' &&
                    mt !== 'LOWER_HAND' &&
                    mt !== 'MOD_UNRAISE_HAND'
                  );
                })
                .map((msg: any) => ({
                id: msg.id,
                userId: msg.userId,
                username: msg.username,
                message: msg.message,
                timestamp: new Date(msg.createdAt),
                isLocal: msg.userId === userId,
                isIntersectStaff: msg.isIntersectStaff,
                chatType: (msg.chatType || 'default') as ChatMessageType
              }));
              
              // Store timestamps for reactions API
              const timestamps: Record<string, number> = {};
              rawHistory.forEach((msg: any) => {
                timestamps[msg.id] = msg.timestamp;
              });
              setMessageTimestamps(timestamps);
              
              // Convert API reactions format to local format
              const historicalReactions: MessageReactions = {};
              rawHistory.forEach((msg: any) => {
                if (msg.reactions) {
                  historicalReactions[msg.id] = {
                    thumbs_up: msg.reactions.thumbs_up || [],
                    thumbs_down: msg.reactions.thumbs_down || [],
                    heart: msg.reactions.heart || [],
                    celebrate: msg.reactions.celebrate || [],
                    wave: msg.reactions.wave || [],
                    laugh: msg.reactions.laugh || [],
                    exclamation: msg.reactions.exclamation || []
                  };
                }
              });
              
              setMessages(historicalMessages);
              setReactions(historicalReactions);
              logger.log('[IVS Chat] Loaded', historicalMessages.length, 'historical messages');
            }
          } catch (error) {
            console.error('[IVS Chat] Error fetching chat history:', error);
          }
        };

        // Set up event listeners
        unsubscribeFunctions.push(
          chatRoom.addListener('connect', () => {
            logger.log('Chat room connected');
            setIsConnected(true);
            
            // Fetch historical messages when connected
            fetchChatHistory();
          })
        );

        unsubscribeFunctions.push(
          chatRoom.addListener('disconnect', () => {
            logger.log('Chat room disconnected');
            setIsConnected(false);
          })
        );

        // Listen for message deletions from AWS IVS Chat
        unsubscribeFunctions.push(
          chatRoom.addListener('messageDelete', (deleteEvent: any) => {
            // The messageId field contains the actual message ID to delete
            // The id field is the delete operation ID (not what we need)
            const deletedMessageId = deleteEvent.messageId || 
                                    deleteEvent.MessageId ||
                                    deleteEvent.attributes?.MessageId ||
                                    deleteEvent.attributes?.messageId ||
                                    deleteEvent.id || 
                                    deleteEvent.Id;
            logger.log('[IVS Chat] Received messageDelete event for:', deletedMessageId, 'Full event:', deleteEvent);
            
            if (deletedMessageId) {
              setMessages(prev => {
                logger.log('[IVS Chat] Current message IDs in state:', prev.map(m => ({ id: m.id, content: m.message.substring(0, 20) })));
                const messageExists = prev.some(msg => msg.id === deletedMessageId);
                logger.log('[IVS Chat] Message exists in local state?', messageExists);
                return prev.filter(msg => msg.id !== deletedMessageId);
              });
            }
          })
        );

        const resolveChatSenderProfileIfNeeded = (
          messageId: string,
          senderId: string,
          provisionalName: string
        ) => {
          if (!accessToken || !messageId || senderId === 'Unknown') return;
          if (!INTERSECT_USER_ID_RE.test(senderId)) return;
          const p = (provisionalName || '').trim();
          if (p && p !== senderId && !INTERSECT_USER_ID_RE.test(p)) return;
          API.getIntersectUserProfile(accessToken, { userId: senderId })
            .then(async (res) => {
              if (!res.ok) return;
              const data = await res.json();
              const un = typeof data?.username === 'string' ? data.username.trim() : '';
              if (!un) return;
              setMessages((prev) =>
                prev.map((m) => (m.id === messageId ? { ...m, username: un } : m))
              );
            })
            .catch(() => {
              /* ignore */
            });
        };

        // Listen for messages - try multiple event names
        const handleMessage = (message: any) => {
          logger.log('Received chat message:', message);
          
          // Handle IVS Chat message structure: can be Sender (uppercase) or sender (lowercase)
          // Never use Attributes.username as userId — it breaks mod teleport / targeting (UUID vs display name).
          const messageUserId =
            message.Sender?.UserId ||
            message.sender?.userId ||
            message.Sender?.Attributes?.userId ||
            message.sender?.attributes?.userId ||
            message.attributes?.userId ||
            message.senderId ||
            message.userId ||
            message.sender?.id ||
            'Unknown';
          
          const messageContent = message.Content || 
                                message.content || 
                                message.text || 
                                message.message || 
                                message.body || 
                                message.payload?.content || '';
          
          // Check if this message is from the current user
          // Extract username to compare
          const messageUsername = message.Sender?.Attributes?.username || 
                                 message.sender?.attributes?.username ||
                                 message.Sender?.Attributes?.userId ||
                                 message.sender?.attributes?.userId ||
                                 message.sender?.userId ||
                                 messageUserId;
          
          const isFromCurrentUser = messageUserId === userId || 
                                   message.Sender?.UserId === userId ||
                                   message.Sender?.Attributes?.userId === userId ||
                                   message.sender?.userId === userId ||
                                   message.sender?.attributes?.userId === userId ||
                                   message.senderId === userId ||
                                   messageUsername === userId ||
                                   (localUsername !== 'Unknown' && messageUsername === localUsername);

          // Check if sender is Intersect staff - try both uppercase and lowercase paths
          const intersectStaffStr = message.Sender?.Attributes?.intersectStaff || 
                                   message.sender?.attributes?.intersectStaff ||
                                   message.attributes?.intersectStaff || 
                                   'false';
          const isIntersectStaff = intersectStaffStr === 'true' || intersectStaffStr === true;

          // Extract chat type from message attributes (for announcements/warnings)
          const chatTypeFromMsg = message.Attributes?.chatType || 
                                 message.attributes?.chatType ||
                                 message.Sender?.Attributes?.chatType ||
                                 'default';

          const messageType =
            message.Attributes?.messageType ||
            message.attributes?.messageType ||
            message.Attributes?.MessageType ||
            message.attributes?.MessageType;

          const commandType = String(messageType ?? '')
            .trim()
            .toUpperCase();

          const handKind = commandType;

          // Raise / lower hand — no visible chat line; optional empty content
          if (handKind === 'RAISE_HAND' || handKind === 'LOWER_HAND') {
            if (!isFromCurrentUser) {
              const senderUserId =
                message.sender?.attributes?.userId ||
                message.Sender?.Attributes?.userId ||
                message.sender?.userId ||
                message.Sender?.UserId ||
                message.userId ||
                message.senderId ||
                messageUserId;

              let contentMeta: { displayName?: string; username?: string } | null = null;
              try {
                const c = typeof messageContent === 'string' ? messageContent.trim() : '';
                if (c.startsWith('{')) {
                  const parsed = JSON.parse(c) as Record<string, unknown>;
                  if (parsed && typeof parsed === 'object') {
                    contentMeta = {
                      displayName:
                        typeof parsed.displayName === 'string' ? parsed.displayName : undefined,
                      username: typeof parsed.username === 'string' ? parsed.username : undefined,
                    };
                  }
                }
              } catch {
                /* ignore malformed JSON */
              }

              const attrDisplay =
                message.Attributes?.displayName ||
                message.attributes?.displayName ||
                message.Attributes?.DisplayName ||
                message.attributes?.DisplayName ||
                message.Sender?.Attributes?.displayName ||
                message.sender?.attributes?.displayName ||
                message.Sender?.Attributes?.DisplayName ||
                message.sender?.attributes?.DisplayName;
              const senderAttrUsername =
                message.Sender?.Attributes?.username ||
                message.sender?.attributes?.username ||
                message.Attributes?.username ||
                message.attributes?.username;
              const displayName =
                (attrDisplay && String(attrDisplay).trim()) ||
                (senderAttrUsername && String(senderAttrUsername).trim()) ||
                (contentMeta?.displayName && String(contentMeta.displayName).trim()) ||
                (contentMeta?.username && String(contentMeta.username).trim()) ||
                messageUsername ||
                senderUserId;
              const at =
                typeof message.SendTime === 'number'
                  ? message.SendTime
                  : message.sendTime
                    ? new Date(message.sendTime).getTime()
                    : Date.now();
              onHandRaiseSignalRef.current?.({
                userId: String(senderUserId),
                displayName: String(displayName),
                raised: handKind === 'RAISE_HAND',
                at,
              });
            }
            return;
          }

          // Breakout clap — no visible chat line; local echo comes from sendBreakoutClap
          if (handKind === 'BREAKOUT_CLAP') {
            if (!isFromCurrentUser) {
              const senderUserId =
                message.sender?.attributes?.userId ||
                message.Sender?.Attributes?.userId ||
                message.sender?.userId ||
                message.Sender?.UserId ||
                message.userId ||
                message.senderId ||
                messageUserId;

              let contentMeta: { displayName?: string; username?: string } | null = null;
              try {
                const c = typeof messageContent === 'string' ? messageContent.trim() : '';
                if (c.startsWith('{')) {
                  const parsed = JSON.parse(c) as Record<string, unknown>;
                  if (parsed && typeof parsed === 'object') {
                    contentMeta = {
                      displayName:
                        typeof parsed.displayName === 'string' ? parsed.displayName : undefined,
                      username: typeof parsed.username === 'string' ? parsed.username : undefined,
                    };
                  }
                }
              } catch {
                /* ignore malformed JSON */
              }

              const attrDisplay =
                message.Attributes?.displayName ||
                message.attributes?.displayName ||
                message.Attributes?.DisplayName ||
                message.attributes?.DisplayName ||
                message.Sender?.Attributes?.displayName ||
                message.sender?.attributes?.displayName ||
                message.Sender?.Attributes?.DisplayName ||
                message.sender?.attributes?.DisplayName;
              const senderAttrUsername =
                message.Sender?.Attributes?.username ||
                message.sender?.attributes?.username ||
                message.Attributes?.username ||
                message.attributes?.username;
              const displayName =
                (attrDisplay && String(attrDisplay).trim()) ||
                (senderAttrUsername && String(senderAttrUsername).trim()) ||
                (contentMeta?.displayName && String(contentMeta.displayName).trim()) ||
                (contentMeta?.username && String(contentMeta.username).trim()) ||
                messageUsername ||
                senderUserId;
              const at =
                typeof message.SendTime === 'number'
                  ? message.SendTime
                  : message.sendTime
                    ? new Date(message.sendTime).getTime()
                    : Date.now();
              onClapSignalRef.current?.({
                userId: String(senderUserId),
                displayName: String(displayName),
                at,
              });
            }
            return;
          }

          const normId = (v: unknown) => (v == null || v === '' ? '' : String(v).trim());

          const isTargetForModCommand = (target: unknown) => {
            const t = normId(target);
            if (!t) return false;
            const uid = userId ? normId(userId) : '';
            const chatId = ivsChatUserIdRef.current ? normId(ivsChatUserIdRef.current) : '';
            if (!uid && !chatId) return false;
            if (uid && t === uid) return true;
            // Remote messages use IVS chat user id (Intersect username from chat token), not participant UUID
            if (chatId && t === chatId) return true;
            if (localUsername && localUsername !== 'Unknown' && t === normId(localUsername)) return true;
            const hr = handRaiseDisplayName?.trim();
            if (hr && t === hr) return true;
            return false;
          };

          let contentPeekType: string | undefined;
          if (typeof messageContent === 'string' && messageContent.trim().startsWith('{')) {
            try {
              contentPeekType = (JSON.parse(messageContent) as { type?: string }).type;
            } catch {
              /* ignore */
            }
          }

          const isModTeleportMessage =
            commandType === 'MOD_TELEPORT' || contentPeekType === 'MOD_TELEPORT';

          // Mod commands — JSON body is reliable; custom attributes are not always echoed to recipients
          if (isModTeleportMessage && messageContent) {
            try {
              const teleportData = JSON.parse(messageContent) as {
                targetUserId?: string;
                position?: { x: number; y: number; z: number };
                rotation?: { x: number; y: number; z: number };
              };
              const targetUserId =
                message.Attributes?.targetUserId ||
                message.attributes?.targetUserId ||
                message.Attributes?.TargetUserId ||
                message.attributes?.TargetUserId ||
                teleportData.targetUserId;

              if (isTargetForModCommand(targetUserId)) {
                logger.log('[IVS Chat] 📍 Received moderator teleport command for me!', { targetUserId });

                if (teleportData.position && window.sendMessageToUnity) {
                  logger.log('[IVS Chat→Unity] Executing local teleport to podium:', teleportData.position);

                  const teleportPayload = {
                    eventType: 'Teleport',
                    payload: JSON.stringify({
                      position: teleportData.position,
                      rotation: teleportData.rotation || { x: 0, y: 0, z: 0 },
                    }),
                  };

                  window.sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(teleportPayload));
                  logger.log('[IVS Chat→Unity] Local teleport command sent to Unity');

                  if (onPodiumTeleport) {
                    onPodiumTeleport();
                  }
                }
              } else {
                logger.log('[IVS Chat] Received moderator teleport command for another user:', targetUserId);
              }
            } catch (error) {
              console.error('[IVS Chat] Error handling moderator teleport:', error);
            }
            return;
          }

          const isModUnraiseHandMessage =
            commandType === 'MOD_UNRAISE_HAND' || contentPeekType === 'MOD_UNRAISE_HAND';

          if (isModUnraiseHandMessage && messageContent) {
            try {
              const unraiseData = JSON.parse(messageContent) as {
                targetUserId?: string;
                type?: string;
              };
              const targetUserId =
                message.Attributes?.targetUserId ||
                message.attributes?.targetUserId ||
                message.Attributes?.TargetUserId ||
                message.attributes?.TargetUserId ||
                unraiseData.targetUserId;
              if (targetUserId) {
                const tid = String(targetUserId).trim();
                logger.log('[IVS Chat] MOD_UNRAISE_HAND received', { targetUserId: tid });
                onHandRaiseSignalRef.current?.({
                  userId: tid,
                  displayName: '',
                  raised: false,
                  at: Date.now(),
                });
              }
            } catch (error) {
              console.error('[IVS Chat] Error handling MOD_UNRAISE_HAND:', error);
            }
            return;
          }

          if (!messageContent) {
            logger.warn('Message has no content, full message:', JSON.stringify(message, null, 2));
            return;
          }

          // Handle moderator grant screen share command
          if (commandType === 'MOD_GRANT_SCREENSHARE') {
            try {
              let grantTarget: string | undefined;
              try {
                const parsed = JSON.parse(messageContent) as { targetUserId?: string };
                grantTarget = parsed.targetUserId;
              } catch {
                /* ignore */
              }
              const targetUserId =
                message.Attributes?.targetUserId ||
                message.attributes?.targetUserId ||
                message.Attributes?.TargetUserId ||
                message.attributes?.TargetUserId ||
                grantTarget;

              if (isTargetForModCommand(targetUserId)) {
                logger.log('[IVS Chat] 🎥 Received screen share permission grant!');

                if (onScreenSharePermissionGranted) {
                  onScreenSharePermissionGranted();
                }
              } else {
                logger.log('[IVS Chat] Received screen share grant for another user:', targetUserId);
              }
            } catch (error) {
              console.error('[IVS Chat] Error handling screen share grant:', error);
            }
            return;
          }
          
          // Handle moderator revoke screen share command
          if (commandType === 'MOD_REVOKE_SCREENSHARE') {
            try {
              let revokeTarget: string | undefined;
              try {
                const parsed = JSON.parse(messageContent) as { targetUserId?: string };
                revokeTarget = parsed.targetUserId;
              } catch {
                /* ignore */
              }
              const targetUserId =
                message.Attributes?.targetUserId ||
                message.attributes?.targetUserId ||
                message.Attributes?.TargetUserId ||
                message.attributes?.TargetUserId ||
                revokeTarget;

              if (isTargetForModCommand(targetUserId)) {
                logger.log('[IVS Chat] 🚫 Received screen share permission revoke!');
                
                // Notify parent that user's screen share permission is revoked
                if (onScreenSharePermissionRevoked) {
                  onScreenSharePermissionRevoked();
                }
              } else {
                logger.log('[IVS Chat] Received screen share revoke for another user:', targetUserId);
              }
            } catch (error) {
              console.error('[IVS Chat] Error handling screen share revoke:', error);
            }
            // Don't add this as a visible chat message
            return;
          }
          
          // Handle reaction messages
          if (messageType === 'REACTION') {
            try {
              const targetMessageId = message.Attributes?.targetMessageId || 
                                     message.attributes?.targetMessageId;
              const reactionType = (message.Attributes?.reactionType || 
                                   message.attributes?.reactionType) as ReactionType;
              const reactionUserId = message.Attributes?.userId || 
                                    message.attributes?.userId;
              const reactionUsername = message.Attributes?.username || 
                                      message.attributes?.username;
              
              // Skip if this is from the current user (we already have optimistic update)
              const isFromCurrentUser = reactionUserId === userId || 
                                       reactionUserId === localUsername;
              
              if (!isFromCurrentUser && targetMessageId && reactionType) {
                logger.log('[IVS Chat] Received reaction from another user:', { targetMessageId, reactionType, reactionUserId });
                
                setReactions(prev => {
                  const messageReactions = prev[targetMessageId] || {
                    thumbs_up: [],
                    thumbs_down: [],
                    heart: [],
                    celebrate: [],
                    wave: [],
                    laugh: [],
                    exclamation: []
                  };
                  
                  // Check if user already reacted with this type
                  const existingReaction = messageReactions[reactionType].find(
                    r => r.userId === reactionUserId
                  );
                  
                  if (existingReaction) {
                    // Remove reaction (toggle off)
                    logger.log('[IVS Chat] Removing reaction:', reactionType, 'from user:', reactionUserId);
                    return {
                      ...prev,
                      [targetMessageId]: {
                        ...messageReactions,
                        [reactionType]: messageReactions[reactionType].filter(
                          r => r.userId !== reactionUserId
                        )
                      }
                    };
                  } else {
                    // Add reaction
                    logger.log('[IVS Chat] Adding reaction:', reactionType, 'from user:', reactionUserId);
                    return {
                      ...prev,
                      [targetMessageId]: {
                        ...messageReactions,
                        [reactionType]: [
                          ...messageReactions[reactionType],
                          {
                            type: reactionType,
                            userId: reactionUserId,
                            username: reactionUsername
                          }
                        ]
                      }
                    };
                  }
                });
              } else if (isFromCurrentUser) {
                logger.log('[IVS Chat] Skipping reaction from current user (already have optimistic update)');
              }
            } catch (e) {
              logger.warn('Failed to process reaction:', e);
            }
            return; // Don't display reactions as regular messages
          }
          
          // Also check for reaction in content format [REACTION:messageId:reactionType]
          if (messageContent.startsWith('[REACTION:') && messageContent.endsWith(']')) {
            try {
              const parts = messageContent.substring(10, messageContent.length - 1).split(':');
              if (parts.length >= 2) {
                const targetMessageId = parts[0];
                const reactionType = parts[1] as ReactionType;
                logger.log('[IVS Chat] Received reaction from content format:', { targetMessageId, reactionType });
              }
            } catch (e) {
              logger.warn('Failed to parse reaction from content:', e);
            }
            return; // Don't display reactions as regular messages
          }

          // Extract message ID first (before we use it)
          const messageId = message.Id || 
                           message.id || 
                           message.messageId || 
                           message.RequestId ||
                           message.requestId || 
                           message.eventId || 
                           `${messageUserId}-${Date.now()}-${Math.random()}`;
          
          logger.log('[IVS Chat] Storing message with ID:', messageId, 'Raw message fields:', {
            Id: message.Id,
            id: message.id,
            messageId: message.messageId,
            RequestId: message.RequestId
          });

          // Check if this might be a server echo of a recently sent local message
          // Look for a local message with the same content sent within the last 5 seconds
          const now = new Date();
          let foundLocalMessage = false;
          
            setMessages(prev => {
            // Find recent local message with matching content (within 5 seconds)
            const recentLocalMessage = prev.find(msg => 
              msg.isLocal && 
              msg.message === messageContent &&
              (now.getTime() - msg.timestamp.getTime()) < 5000
            );
            
            if (recentLocalMessage) {
              foundLocalMessage = true;
              // Update the local message with server info (including the real server ID!)
              logger.log('[IVS Chat] Updating local message ID from', recentLocalMessage.id, 'to server ID', messageId);
              
              // Also migrate any reactions from the old ID to the new ID
              const oldId = recentLocalMessage.id;
              if (oldId !== messageId) {
                setReactions(prevReactions => {
                  if (prevReactions[oldId]) {
                    const { [oldId]: oldReactions, ...rest } = prevReactions;
                    return {
                      ...rest,
                      [messageId]: oldReactions
                    };
                  }
                  return prevReactions;
                });
              }
              
              const echoedLabel = senderDisplayLabelFromIvsMessage(message, messageUserId);
              const idStr = String(messageUserId).trim();
              const echoLabelIsOnlyId =
                !echoedLabel ||
                echoedLabel === 'Unknown' ||
                (INTERSECT_USER_ID_RE.test(echoedLabel) && echoedLabel === idStr);
              const nextUsername = echoLabelIsOnlyId ? recentLocalMessage.username : echoedLabel;

              return prev.map((msg) =>
                msg.id === recentLocalMessage.id
                  ? {
                      ...msg,
                      id: messageId,
                      username: nextUsername,
                      isIntersectStaff: isIntersectStaff,
                      chatType: (chatTypeFromMsg as ChatMessageType) || msg.chatType,
                    }
                  : msg
              );
            }
            return prev;
            });
            
          // If we found and updated a local message, skip adding this as a new message
          if (foundLocalMessage) {
            // Update local display name only when IVS sent a real label (avoid storing raw user id as "username")
            if (isFromCurrentUser) {
              const selfLabel = senderDisplayLabelFromIvsMessage(message, messageUserId);
              if (
                selfLabel &&
                selfLabel !== 'Unknown' &&
                !(INTERSECT_USER_ID_RE.test(selfLabel) && selfLabel === String(messageUserId).trim())
              ) {
                setLocalUsername(selfLabel);
              }
            }
            if (isFromCurrentUser && isIntersectStaff) {
              setLocalIsIntersectStaffFromAPI(true);
            }
            return;
          }

          // If this is from the current user but no local message was found, update user info
          if (isFromCurrentUser) {
            const selfLabel2 = senderDisplayLabelFromIvsMessage(message, messageUserId);
            if (
              selfLabel2 &&
              selfLabel2 !== 'Unknown' &&
              !(INTERSECT_USER_ID_RE.test(selfLabel2) && selfLabel2 === String(messageUserId).trim())
            ) {
              setLocalUsername(selfLabel2);
            }
            if (isIntersectStaff) {
              setLocalIsIntersectStaffFromAPI(true);
            }
            // Still skip if it's from current user (edge case where local message wasn't added yet)
            return;
          }
          
          // Check if we already have this message (avoid duplicates)
          setMessages(prev => {
            // Check by ID first
            const existsById = prev.find(msg => msg.id === messageId);
            if (existsById) {
              logger.log('Message already exists by ID, skipping:', messageId);
              return prev;
            }

            // Also check by content and timestamp to avoid duplicates from different sources
            const sendTime = message.SendTime || message.sendTime || message.timestamp;
            const messageTimestamp = sendTime ? new Date(sendTime) : new Date();

            const displayUsername = senderDisplayLabelFromIvsMessage(message, messageUserId);

            // All server messages appear on the left (as received messages)
            const newMessage: ChatMessage = {
              id: messageId,
              userId: messageUserId,
              username: displayUsername,
              message: messageContent,
              timestamp: messageTimestamp,
              isLocal: false, // Server messages always appear on left
              isIntersectStaff,
              chatType: chatTypeFromMsg as ChatMessageType
            };

            logger.log('Adding new message:', newMessage);
            
            // Store timestamp for reactions API
            const timestampMs = messageTimestamp.getTime();
            setMessageTimestamps(prevTimestamps => ({ ...prevTimestamps, [messageId]: timestampMs }));
            
            // Increment unread count if chat is closed and we haven't counted this message yet
            if (!isOpenRef.current && !countedMessageIds.current.has(messageId)) {
              countedMessageIds.current.add(messageId);
              setUnreadCount(prev => prev + 1);
            }
            
            return [...prev, newMessage];
          });

          queueMicrotask(() => {
            resolveChatSenderProfileIfNeeded(
              String(messageId),
              String(messageUserId),
              senderDisplayLabelFromIvsMessage(message, messageUserId)
            );
          });
        };

        unsubscribeFunctions.push(chatRoom.addListener('message', handleMessage));
        
        // Listen for events (reactions) - LEGACY FALLBACK
        // Note: Reactions are now sent as regular messages with special attributes
        // This listener is kept as a fallback in case AWS adds native event support
        const handleEvent = (event: any) => {
          logger.log('Received chat event:', event);
          logger.log('Event keys:', Object.keys(event));
          logger.log('Full event structure:', JSON.stringify(event, null, 2));
          
          // Handle both EVENT type and direct event objects
          const eventType = event.Type || event.type;
          const eventName = event.EventName || event.eventName || event.Event;
          const attributes = event.Attributes || event.attributes || {};
          
          // Also check Sender for user info
          const senderUserId = event.Sender?.UserId || 
                              event.Sender?.Attributes?.userId ||
                              event.senderId ||
                              event.userId;
          
          logger.log('Event details:', { eventType, eventName, attributes, senderUserId });
          
          if ((eventType === 'EVENT' || !eventType) && eventName === 'message_reaction') {
            const messageId = attributes.messageId || attributes.MessageId || attributes.message_id;
            const reactionType = attributes.reactionType || attributes.reaction_type;
            const reactionUserId = attributes.userId || attributes.user_id || senderUserId;
            const reactionUsername = attributes.username || attributes.display_name || reactionUserId;
            
            logger.log('Processing reaction event:', { messageId, reactionType, reactionUserId, reactionUsername });
            
            // Skip if this is from the current user (we already have optimistic update)
            const isFromCurrentUser = reactionUserId === userId || 
                                     reactionUserId === localUsername ||
                                     senderUserId === userId ||
                                     senderUserId === localUsername;
            
            if (isFromCurrentUser) {
              logger.log('Skipping reaction event from current user (already have optimistic update)');
              return;
            }
            
            if (messageId && reactionType && (reactionType === 'thumbs_up' || reactionType === 'thumbs_down' || reactionType === 'heart' || reactionType === 'celebrate' || reactionType === 'wave' || reactionType === 'laugh' || reactionType === 'exclamation')) {
              setReactions(prev => {
                const messageReactions = prev[messageId] || {
                  thumbs_up: [],
                  thumbs_down: [],
                  heart: [],
                  celebrate: [],
                  wave: [],
                  laugh: [],
                  exclamation: []
                };
                
                // Check if user already reacted with this type
                const existingReaction = messageReactions[reactionType as ReactionType].find(
                  r => r.userId === reactionUserId
                );
                
                if (existingReaction) {
                  // Remove reaction (toggle off)
                  logger.log('Removing reaction:', reactionType, 'from user:', reactionUserId);
                  return {
                    ...prev,
                    [messageId]: {
                      ...messageReactions,
                      [reactionType]: messageReactions[reactionType as ReactionType].filter(
                        r => r.userId !== reactionUserId
                      )
                    }
                  };
                } else {
                  // Add reaction
                  logger.log('Adding reaction:', reactionType, 'from user:', reactionUserId);
                  return {
                    ...prev,
                    [messageId]: {
                      ...messageReactions,
                      [reactionType]: [
                        ...messageReactions[reactionType as ReactionType],
                        {
                          type: reactionType as ReactionType,
                          userId: reactionUserId,
                          username: reactionUsername
                        }
                      ]
                    }
                  };
                }
              });
            } else {
              logger.warn('Invalid reaction event data:', { messageId, reactionType });
            }
          } else {
            logger.log('Event is not a message_reaction event:', { eventType, eventName });
          }
        };
        
        unsubscribeFunctions.push(chatRoom.addListener('event', handleEvent));
        
        // Also try other possible event names
        if (typeof (chatRoom as any).addListener === 'function') {
          try {
            unsubscribeFunctions.push((chatRoom as any).addListener('messageReceived', handleMessage));
            unsubscribeFunctions.push((chatRoom as any).addListener('chatMessage', handleMessage));
            unsubscribeFunctions.push((chatRoom as any).addListener('eventReceived', handleEvent));
          } catch (e) {
            // Ignore if these events don't exist
          }
        }

        // Connect to the chat room
        await chatRoom.connect();

      } catch (error) {
        console.error('Error connecting to IVS chat room:', error);
        setIsConnected(false);
      }
    };

    connectToChatRoom();

    // Cleanup
    return () => {
      logger.log('[IVS Chat] Cleaning up chat connection...');
      
      // Unsubscribe from all event listeners
      unsubscribeFunctions.forEach(unsubscribe => {
        try {
          unsubscribe();
        } catch (e) {
          console.error('[IVS Chat] Error unsubscribing:', e);
        }
      });
      
      if (chatRoomRef.current) {
        try {
          logger.log('[IVS Chat] Disconnecting from chat room...');
          chatRoomRef.current.disconnect();
          logger.log('[IVS Chat] Disconnected from chat room');
        } catch (error) {
          console.error('[IVS Chat] Error disconnecting chat room:', error);
        }
        chatRoomRef.current = null;
      }
      
      // Clear typing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      logger.log('[IVS Chat] Cleanup complete');
    };
  }, [roomArn, messagingEndpoint, accessToken, userId]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !isConnected || !chatRoomRef.current) return;

    const messageText = inputMessage.trim();
    setInputMessage('');

    // Clear typing state when sending message
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    if (isTypingRef.current) {
      isTypingRef.current = false;
      sendTypingStatusToUnity(false);
    }

    // Add message to local state immediately for better UX
    // Only staff can send non-default message types
    const effectiveChatType = localIsIntersectStaff ? selectedChatType : 'default';
    
    const localMessage: ChatMessage = {
      id: `local-${Date.now()}-${Math.random()}`,
      userId: userId || 'Unknown',
      username: localUsername,
      message: messageText,
      timestamp: new Date(),
      isLocal: true,
      isIntersectStaff: localIsIntersectStaff,
      chatType: effectiveChatType
    };
    setMessages(prev => [...prev, localMessage]);

    // Send chat message to Unity
    if (window.sendMessageToUnity) {
      try {
        const chatData = {
          eventType: 'Chat',
          payload: JSON.stringify({
            message: messageText
          })
        };
        window.sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(chatData));
        logger.log('[Chat→Unity] Sent chat message to Unity:', messageText);
      } catch (error) {
        console.error('[Chat→Unity] Error sending to Unity:', error);
      }
    }

    try {
      logger.log('Sending message:', messageText, 'with chat type:', effectiveChatType);
      // Send message via ChatRoom
      const requestId = `msg-${Date.now()}-${Math.random()}`;
      
      // Build message attributes - only include chatType if not default (to save bandwidth)
      const messageAttributes: Record<string, string> = {};
      if (effectiveChatType !== 'default') {
        messageAttributes.chatType = effectiveChatType;
      }
      
      const result = await chatRoomRef.current.sendMessage({
        id: requestId,
        content: messageText,
        action: 'SEND_MESSAGE',
        requestId: requestId,
        ...(Object.keys(messageAttributes).length > 0 && { attributes: messageAttributes })
      } as any);
      logger.log('Message send result:', result);
      
      // Persist message to backend (fire-and-forget, don't block UI)
      const messageId = result?.id || requestId;
      const timestamp = Date.now();
      
      // Store timestamp for potential reactions
      setMessageTimestamps(prev => ({ ...prev, [messageId]: timestamp }));
      
      // Save to backend
      if (accessToken && eventId && roomId) {
        API.postChatMessage(accessToken, {
          eventId,
          roomId,
          messageId,
          message: messageText,
          chatType: effectiveChatType,
          isIntersectStaff: localIsIntersectStaff
        }).then(async response => {
          if (!response.ok) throw new Error(`Message persistence failed (${response.status})`);
          const saved = await response.json();
          if (typeof saved.timestamp === 'number') {
            setMessageTimestamps(prev => ({ ...prev, [messageId]: saved.timestamp }));
          }
        }).catch(err => console.error('[IVS Chat] Error persisting message:', err));
      }
      
      // Note: Unity capture is re-enabled by onBlur event when input loses focus
    } catch (error) {
      console.error('Error sending message:', error);
      // Remove the optimistic message if send failed
      setMessages(prev => prev.filter(msg => msg.id !== localMessage.id));
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    if (!localIsIntersectStaff || !roomId || !eventId || !accessToken) return;

    setIsDeletingMessage(true);
    try {
      // Call the backend API endpoint to delete the message via AWS IVS Chat API
      const response = await API.deleteIVSChatMessage(accessToken, {
        eventId: eventId,
        roomId: roomId,
        id: messageId,
        reason: 'Moderator action'
      });

      if (response.status !== 200) {
        throw new Error(`Failed to delete message: ${response.status}`);
      }

      const data = await response.json();
      logger.log('[IVS Chat] Message deleted from IVS, operation ID:', data.id);

      // Also delete from persistent storage (fire-and-forget)
      API.deleteChatMessage(accessToken, {
        eventId,
        roomId,
        messageId,
        reason: 'Moderator action'
      }).then(() => {
        logger.log('[IVS Chat] Message deleted from persistent storage:', messageId);
      }).catch(err => {
        console.error('[IVS Chat] Error deleting message from persistent storage:', err);
      });

      // Remove message locally (AWS will also send a messageDelete event to all clients)
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
      setDeleteConfirmMessageId(null);
    } catch (error) {
      console.error('Error deleting message:', error);
      // Optionally show an error message to the user
    } finally {
      setIsDeletingMessage(false);
    }
  };

  const handleTeleportUser = (message: ChatMessage) => {
    if (!localIsIntersectStaff || !podiumPosition) return;
    setTeleportConfirmMessage(message);
  };

  const handleConfirmTeleport = async () => {
    if (!teleportConfirmMessage) return;

    try {
      await executeModeratorTeleport(teleportConfirmMessage.userId);
    } catch (error) {
      console.error('[IVS Chat] Error sending teleport command:', error);
    }

    setTeleportConfirmMessage(null);
  };

  const handleCancelTeleport = () => {
    setTeleportConfirmMessage(null);
  };

  const handleGrantScreenShare = (message: ChatMessage) => {
    if (!localIsIntersectStaff) return;
    setScreenShareConfirmMessage(message);
  };

  const handleConfirmGrantScreenShare = async () => {
    if (!screenShareConfirmMessage) return;
    try {
      await executeModeratorGrantScreenShare(screenShareConfirmMessage.userId);
    } catch (error) {
      console.error('[IVS Chat] Error sending screen share grant:', error);
    }
    setScreenShareConfirmMessage(null);
  };

  const handleCancelGrantScreenShare = () => {
    setScreenShareConfirmMessage(null);
  };

  const handleRevokeScreenShare = (message: ChatMessage) => {
    if (!localIsIntersectStaff) return;
    setScreenShareRevokeConfirmMessage(message);
  };

  const handleConfirmRevokeScreenShare = async () => {
    if (!screenShareRevokeConfirmMessage) return;
    try {
      await executeModeratorRevokeScreenShare(screenShareRevokeConfirmMessage.userId);
    } catch (error) {
      console.error('[IVS Chat] Error sending screen share revoke:', error);
    }
    setScreenShareRevokeConfirmMessage(null);
  };

  const handleCancelRevokeScreenShare = () => {
    setScreenShareRevokeConfirmMessage(null);
  };

  const handleReaction = async (messageId: string, reactionType: ReactionType) => {
    if (!isConnected || !chatRoomRef.current) return;

    const currentUserId = userId || localUsername;
    
    // Optimistically update reactions immediately
    setReactions(prev => {
      const messageReactions = prev[messageId] || {
        thumbs_up: [],
        thumbs_down: [],
        heart: [],
        celebrate: [],
        wave: [],
        laugh: [],
        exclamation: []
      };
      
      // Check if user already reacted with this type
      const existingReaction = messageReactions[reactionType].find(
        r => r.userId === currentUserId
      );
      
      if (existingReaction) {
        // Remove reaction (toggle off)
        return {
          ...prev,
          [messageId]: {
            ...messageReactions,
            [reactionType]: messageReactions[reactionType].filter(
              r => r.userId !== currentUserId
            )
          }
        };
      } else {
        // Add reaction
        return {
          ...prev,
          [messageId]: {
            ...messageReactions,
            [reactionType]: [
              ...messageReactions[reactionType],
              {
                type: reactionType,
                userId: currentUserId,
                username: localUsername
              }
            ]
          }
        };
      }
    });

    try {
      // Send reaction as a regular message with special attributes (like delete commands)
      const requestId = `reaction-${Date.now()}-${Math.random()}`;
      await chatRoomRef.current.sendMessage({
        id: requestId,
        content: `[REACTION:${messageId}:${reactionType}]`, // Special content format
        action: 'SEND_MESSAGE',
        requestId: requestId,
        attributes: {
          messageType: 'REACTION',
          targetMessageId: messageId,
          reactionType: reactionType,
          userId: currentUserId,
          username: localUsername
        }
      } as any);
      
      logger.log('[IVS Chat] Sent reaction:', { messageId, reactionType, userId: currentUserId });
      
      // Persist reaction to backend (fire-and-forget)
      const timestamp = messageTimestamps[messageId];
      if (timestamp && accessToken && eventId && roomId) {
        API.postChatReaction(accessToken, {
          eventId,
          roomId,
          messageId,
          timestamp,
          reactionType
        }).then(response => {
          if (!response.ok) throw new Error(`Reaction persistence failed (${response.status})`);
        }).catch(err => console.error('[IVS Chat] Error persisting reaction:', err));
      }
    } catch (error) {
      console.error('Error sending reaction:', error);
      // Revert optimistic update on error
      setReactions(prev => {
        const messageReactions = prev[messageId];
        if (!messageReactions) return prev;
        
        const existingReaction = messageReactions[reactionType].find(
          r => r.userId === currentUserId
        );
        
        if (existingReaction) {
          // Remove the optimistic reaction we just added
          return {
            ...prev,
            [messageId]: {
              ...messageReactions,
              [reactionType]: messageReactions[reactionType].filter(
                r => r.userId !== currentUserId
              )
            }
          };
        } else {
          // Re-add the reaction we just removed
          return {
            ...prev,
            [messageId]: {
              ...messageReactions,
              [reactionType]: [
                ...messageReactions[reactionType],
                {
                  type: reactionType,
                  userId: currentUserId,
                  username: localUsername
                }
              ]
            }
          };
        }
      });
    }
  };

  if (!roomArn || !messagingEndpoint) {
    return null;
  }

  return (
    <>
      {/* Chat Button - Bottom Left */}
      {!isOpen && (
        <button
          onClick={(e) => {
            e.stopPropagation(); // Prevent click from reaching Unity canvas
            setIsOpen(true);
            // Release cursor so it's visible when opening chat (but keep keyboard for movement)
            releaseCursorOnly();
          }}
          className="unity-ivs-glass-fab absolute p-3 text-white rounded-full transition-all duration-200 z-10 flex items-center justify-center"
          style={{
            zIndex: 9999,
            pointerEvents: 'auto',
            bottom: 'var(--unity-dock-tray-bottom)',
            left: 'var(--unity-chat-fab-left)',
          }}
          title="Open chat"
        >
          <FaComments size={20} />
          {/* Unread message badge */}
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-md animate-pulse">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Chat Panel - Bottom Left */}
      {isOpen && (
        <div
          ref={chatContainerRef}
          className="unity-ivs-glass-chat absolute bottom-4 left-4 flex max-h-[calc(100dvh-2rem)] max-w-[calc(100vw-2rem)] flex-col overflow-hidden z-10"
          style={{ zIndex: 9999, pointerEvents: 'auto', width: chatPanelSize.width, height: chatPanelSize.height }}
          data-tour-id={dataTourId}
          onClick={(e) => {
            // Stop propagation to prevent clicks from going to Unity canvas,
            // but don't disable Unity capture here - only do that in the input field onFocus
            e.stopPropagation();
          }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div
            aria-hidden="true"
            className="unity-chat-resize-handle unity-chat-resize-handle--top-left"
            onPointerDown={(event) => beginChatResize(event, -1)}
          />
          <div
            aria-hidden="true"
            className="unity-chat-resize-handle unity-chat-resize-handle--top-right"
            onPointerDown={(event) => beginChatResize(event, 1)}
          />
          {/* Header */}
          <div className="flex justify-between items-center border-b border-white/10 bg-[#2a2a2a] p-3">
            <div className="flex items-center gap-2">
              <h3 className="text-white text-base font-semibold m-0">
                {roomName || 'Chat'}
              </h3>
              <span 
                className={`w-2 h-2 rounded-full ${
                  isConnected 
                    ? 'bg-green-400 shadow-[0_0_4px_rgba(74,222,128,0.5)]' 
                    : 'bg-yellow-500'
                }`}
              ></span>
            </div>
            <button
              onClick={() => {
                setIsOpen(false);
                // Don't manipulate Unity capture here - input field onBlur already handles it
              }}
              className="p-1 text-[#999] hover:text-white transition-colors"
              title="Close chat"
            >
              <FaTimes size={16} />
            </button>
          </div>

          {/* Messages Container */}
          <div
            className="min-h-0 flex-1 overflow-y-auto bg-[#1a1a1a] p-3 space-y-2 select-text [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-[#1a1a1a] [&::-webkit-scrollbar-thumb]:bg-[#444] [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb:hover]:bg-[#555]"
            onContextMenu={(e) => {
              releaseUnityCursorCapture();
              e.stopPropagation();
            }}
          >
            {messages.length === 0 ? (
              <div className="text-center text-[#666] text-sm py-8">
                No messages yet. Start the conversation!
              </div>
            ) : (
              messages.map((msg) => {
                const messageReactions = reactions[msg.id] || {
                  thumbs_up: [],
                  thumbs_down: [],
                  heart: [],
                  celebrate: [],
                  wave: [],
                  laugh: [],
                  exclamation: []
                };
                const hasReactions = messageReactions.thumbs_up.length > 0 || 
                                   messageReactions.thumbs_down.length > 0 || 
                                   messageReactions.heart.length > 0 ||
                                   messageReactions.celebrate.length > 0 ||
                                   messageReactions.wave.length > 0 ||
                                   messageReactions.laugh.length > 0 ||
                                   messageReactions.exclamation.length > 0;
                const isHovered = hoveredMessageId === msg.id;
                const linkTone =
                  msg.chatType === 'announcement'
                    ? 'announcement'
                    : msg.chatType === 'system'
                      ? 'system'
                      : msg.isLocal
                        ? 'local'
                        : 'remote';

                return (
                  <div
                    key={msg.id}
                    className={`flex flex-col ${msg.isLocal ? 'items-end' : 'items-start'}`}
                    onMouseEnter={() => setHoveredMessageId(msg.id)}
                    onMouseLeave={() => setHoveredMessageId(null)}
                  >
                    <div className="relative group">
                      <div
                        className={`min-w-[220px] max-w-[95%] rounded-lg px-3 py-2 ${
                          msg.chatType === 'announcement'
                            ? 'bg-yellow-900/40 border border-yellow-500/50'
                            : msg.chatType === 'system'
                            ? 'bg-red-900/40 border border-red-500/50'
                            : msg.isLocal
                            ? 'bg-blue-600'
                            : 'bg-[#2a2a2a] border border-[#444]'
                        }`}
                      >
                        {/* Header row with username/label on left and timestamp on right */}
                        <div className={`text-xs mb-1 font-medium flex items-center gap-1.5 min-w-0 w-full ${
                          msg.chatType === 'announcement'
                            ? 'text-yellow-300'
                            : msg.chatType === 'system'
                            ? 'text-red-300'
                            : msg.isIntersectStaff 
                            ? 'text-yellow-400' 
                            : (msg.isLocal ? 'text-blue-100' : 'text-[#999]')
                        }`}>
                          {msg.chatType === 'announcement' ? (
                            <>
                              <FaBullhorn className="w-3 h-3 flex-shrink-0 text-yellow-400" />
                              <span className="text-yellow-300 font-semibold">ANNOUNCEMENT</span>
                            </>
                          ) : msg.chatType === 'system' ? (
                            <>
                              <FaExclamationTriangle className="w-3 h-3 flex-shrink-0 text-red-400" />
                              <span className="text-red-300 font-semibold">SYSTEM</span>
                            </>
                          ) : (
                            <>
                              {msg.isIntersectStaff && (
                                <img 
                                  src={intersectStaffIcon} 
                                  alt="Intersect Staff" 
                                  className="w-3 h-3 flex-shrink-0"
                                />
                              )}
                              <span 
                                className="break-words overflow-wrap-anywhere"
                                style={{
                                  fontSize: msg.username.length > 20 ? '0.65rem' : '0.75rem',
                                  lineHeight: '1.2',
                                  wordBreak: 'break-word',
                                  maxWidth: '100%'
                                }}
                              >
                                {msg.username}
                              </span>
                            </>
                          )}
                          {/* Timestamp on the right */}
                          <span className={`ml-auto flex-shrink-0 font-normal ${
                            msg.chatType === 'announcement'
                              ? 'text-yellow-500/70'
                              : msg.chatType === 'system'
                              ? 'text-red-500/70'
                              : msg.isLocal 
                              ? 'text-blue-200/70' 
                              : 'text-[#666]'
                          }`}>
                            {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <div
                          className={`text-sm break-words select-text cursor-text ${
                            msg.chatType === 'announcement'
                              ? 'text-yellow-400'
                              : msg.chatType === 'system'
                                ? 'text-red-400'
                                : 'text-white'
                          }`}
                        >
                          {linkifyChatMessage(msg.message, linkTone)}
                        </div>
                      </div>
                      
                      {/* Persistent reactions - positioned at bottom of message, slightly overlapping */}
                      {hasReactions && (
                        <div className={`absolute ${msg.isLocal ? 'right-2' : 'left-2'} bottom-[-8px] flex items-center gap-1 z-10 pointer-events-auto`}>
                          {messageReactions.thumbs_up.length > 0 && (
                            <button
                              onClick={() => handleReaction(msg.id, 'thumbs_up')}
                              className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs transition-colors hover:bg-[#2a2a2a] bg-[#1a1a1a] border border-[#333]"
                              title="Thumbs up"
                            >
                              <FaThumbsUp className="text-blue-400" size={10} />
                              <span className="text-[#999]">{messageReactions.thumbs_up.length}</span>
                            </button>
                          )}
                          {messageReactions.thumbs_down.length > 0 && (
                            <button
                              onClick={() => handleReaction(msg.id, 'thumbs_down')}
                              className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs transition-colors hover:bg-[#2a2a2a] bg-[#1a1a1a] border border-[#333]"
                              title="Thumbs down"
                            >
                              <FaThumbsDown className="text-red-400" size={10} />
                              <span className="text-[#999]">{messageReactions.thumbs_down.length}</span>
                            </button>
                          )}
                          {messageReactions.heart.length > 0 && (
                            <button
                              onClick={() => handleReaction(msg.id, 'heart')}
                              className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs transition-colors hover:bg-[#2a2a2a] bg-[#1a1a1a] border border-[#333]"
                              title="Heart"
                            >
                              <FaHeart className="text-pink-400" size={10} />
                              <span className="text-[#999]">{messageReactions.heart.length}</span>
                            </button>
                          )}
                          {messageReactions.celebrate.length > 0 && (
                            <button
                              onClick={() => handleReaction(msg.id, 'celebrate')}
                              className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs transition-colors hover:bg-[#2a2a2a] bg-[#1a1a1a] border border-[#333]"
                              title="Celebrate"
                            >
                              <FaRocket className="text-yellow-400" size={10} />
                              <span className="text-[#999]">{messageReactions.celebrate.length}</span>
                            </button>
                          )}
                          {messageReactions.wave.length > 0 && (
                            <button
                              onClick={() => handleReaction(msg.id, 'wave')}
                              className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs transition-colors hover:bg-[#2a2a2a] bg-[#1a1a1a] border border-[#333]"
                              title="Wave"
                            >
                              <FaHandPaper className="text-green-400" size={10} />
                              <span className="text-[#999]">{messageReactions.wave.length}</span>
                            </button>
                          )}
                          {messageReactions.laugh.length > 0 && (
                            <button
                              onClick={() => handleReaction(msg.id, 'laugh')}
                              className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs transition-colors hover:bg-[#2a2a2a] bg-[#1a1a1a] border border-[#333]"
                              title="Laugh"
                            >
                              <FaLaughSquint className="text-amber-400" size={10} />
                              <span className="text-[#999]">{messageReactions.laugh.length}</span>
                            </button>
                          )}
                          {messageReactions.exclamation.length > 0 && (
                            <button
                              onClick={() => handleReaction(msg.id, 'exclamation')}
                              className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs transition-colors hover:bg-[#2a2a2a] bg-[#1a1a1a] border border-[#333]"
                              title="Exclamation"
                            >
                              <FaExclamation className="text-orange-400" size={10} />
                              <span className="text-[#999]">{messageReactions.exclamation.length}</span>
                            </button>
                          )}
                        </div>
                      )}
                      
                      {/* Reaction buttons and mod actions - show on hover above message */}
                      {isHovered && (
                        <div 
                          className={`absolute ${msg.isLocal ? 'left-2' : 'right-2'} -top-4 flex flex-col gap-1 bg-[#1a1a1a] border border-[#333] rounded-lg p-1 shadow-lg z-10 pointer-events-auto`}
                          style={{ transform: localIsIntersectStaff ? 'translateY(-24px)' : 'translateY(0)' }}
                        >
                          {/* Reactions row */}
                          <div className="flex items-center gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReaction(msg.id, 'thumbs_up');
                              }}
                              className={`p-1.5 rounded hover:bg-[#2a2a2a] transition-colors ${
                                messageReactions.thumbs_up.some(r => r.userId === userId || r.userId === localUsername) ? 'bg-[#2a2a2a]' : ''
                              }`}
                              title="Thumbs up"
                            >
                              <FaThumbsUp className="text-blue-400" size={14} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReaction(msg.id, 'thumbs_down');
                              }}
                              className={`p-1.5 rounded hover:bg-[#2a2a2a] transition-colors ${
                                messageReactions.thumbs_down.some(r => r.userId === userId || r.userId === localUsername) ? 'bg-[#2a2a2a]' : ''
                              }`}
                              title="Thumbs down"
                            >
                              <FaThumbsDown className="text-red-400" size={14} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReaction(msg.id, 'heart');
                              }}
                              className={`p-1.5 rounded hover:bg-[#2a2a2a] transition-colors ${
                                messageReactions.heart.some(r => r.userId === userId || r.userId === localUsername) ? 'bg-[#2a2a2a]' : ''
                              }`}
                              title="Heart"
                            >
                              <FaHeart className="text-pink-400" size={14} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReaction(msg.id, 'celebrate');
                              }}
                              className={`p-1.5 rounded hover:bg-[#2a2a2a] transition-colors ${
                                messageReactions.celebrate.some(r => r.userId === userId || r.userId === localUsername) ? 'bg-[#2a2a2a]' : ''
                              }`}
                              title="Celebrate"
                            >
                              <FaRocket className="text-yellow-400" size={14} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReaction(msg.id, 'wave');
                              }}
                              className={`p-1.5 rounded hover:bg-[#2a2a2a] transition-colors ${
                                messageReactions.wave.some(r => r.userId === userId || r.userId === localUsername) ? 'bg-[#2a2a2a]' : ''
                              }`}
                              title="Wave"
                            >
                              <FaHandPaper className="text-green-400" size={14} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReaction(msg.id, 'laugh');
                              }}
                              className={`p-1.5 rounded hover:bg-[#2a2a2a] transition-colors ${
                                messageReactions.laugh.some(r => r.userId === userId || r.userId === localUsername) ? 'bg-[#2a2a2a]' : ''
                              }`}
                              title="Laugh"
                            >
                              <FaLaughSquint className="text-amber-400" size={14} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReaction(msg.id, 'exclamation');
                              }}
                              className={`p-1.5 rounded hover:bg-[#2a2a2a] transition-colors ${
                                messageReactions.exclamation.some(r => r.userId === userId || r.userId === localUsername) ? 'bg-[#2a2a2a]' : ''
                              }`}
                              title="Exclamation"
                            >
                              <FaExclamation className="text-orange-400" size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await navigator.clipboard.writeText(msg.message);
                                  logger.log('[IVS Chat] Copied message to clipboard');
                                } catch (err) {
                                  logger.error('[IVS Chat] Clipboard copy failed:', err);
                                }
                              }}
                              className="p-1.5 rounded hover:bg-[#2a2a2a] transition-colors"
                              title="Copy message"
                            >
                              <FaCopy className="text-[#ccc]" size={14} />
                            </button>
                          </div>
                          
                          {/* Mod actions row - separate row below reactions */}
                          {localIsIntersectStaff && (
                            <div className="flex items-center gap-1 pt-1 border-t border-[#333]">
                              <span className="text-[10px] text-[#666] px-1">MOD:</span>
                              {podiumPosition && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    logger.log('[IVS Chat] Teleport button clicked for user:', msg.userId);
                                    handleTeleportUser(msg);
                                  }}
                                  className="p-1.5 rounded hover:bg-blue-900/50 transition-colors"
                                  title={msg.isLocal ? "Teleport yourself to podium" : "Teleport user to podium"}
                                >
                                  <FaMapMarkerAlt className="text-blue-400" size={14} />
                                </button>
                              )}
                              {/* Grant Screen Share Permission Button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  logger.log('[IVS Chat] Grant screen share clicked for user:', msg.userId);
                                  handleGrantScreenShare(msg);
                                }}
                                className="p-1.5 rounded hover:bg-green-900/50 transition-colors"
                                title={msg.isLocal ? "Grant yourself screen share permission" : "Grant screen share permission"}
                              >
                                <FaDesktop className="text-green-400" size={14} />
                              </button>
                              {/* Revoke Screen Share Permission Button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  logger.log('[IVS Chat] Revoke screen share clicked for user:', msg.userId);
                                  handleRevokeScreenShare(msg);
                                }}
                                className="p-1.5 rounded hover:bg-red-900/50 transition-colors"
                                title={msg.isLocal ? "Revoke your screen share permission" : "Revoke screen share permission"}
                              >
                                <FaDesktop className="text-red-400" size={14} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteConfirmMessageId(msg.id);
                                }}
                                className="p-1.5 rounded hover:bg-red-900/50 transition-colors"
                                title="Delete message"
                              >
                                <FaTrash className="text-red-400" size={14} />
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="border-t border-white/10 bg-[#2a2a2a] p-3">
            <div
              className="flex gap-2 items-center"
              onContextMenu={(e) => {
                if (document.activeElement === inputRef.current) releaseUnityCapture();
                else releaseUnityCursorCapture();
                e.stopPropagation();
              }}
            >
              {/* Chat Type Selector - Only for Staff/Moderators */}
              {localIsIntersectStaff && (
                <div className="relative">
                  <button
                    onClick={() => setShowChatTypeDropdown(!showChatTypeDropdown)}
                    className={`p-2 rounded-md transition-colors flex items-center gap-1 ${
                      selectedChatType === 'announcement' 
                        ? 'bg-yellow-600 text-white hover:bg-yellow-700' 
                        : selectedChatType === 'system'
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : 'bg-[#1a1a1a] border border-[#444] text-[#999] hover:text-white hover:border-[#555]'
                    }`}
                    title={`Chat type: ${selectedChatType === 'default' ? 'Default' : selectedChatType === 'announcement' ? 'Announcement' : 'System'}`}
                  >
                    {selectedChatType === 'announcement' ? (
                      <FaBullhorn size={14} />
                    ) : selectedChatType === 'system' ? (
                      <FaExclamationTriangle size={14} />
                    ) : (
                      <FaComments size={14} />
                    )}
                    <FaChevronDown size={10} className={`transition-transform ${showChatTypeDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Dropdown Menu */}
                  {showChatTypeDropdown && (
                    <div className="unity-ivs-glass-chat-dropdown absolute bottom-full left-0 mb-2 overflow-hidden min-w-[160px] z-20">
                      <button
                        onClick={() => {
                          setSelectedChatType('default');
                          setShowChatTypeDropdown(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-[#2a2a2a] transition-colors ${
                          selectedChatType === 'default' ? 'bg-[#2a2a2a] text-white' : 'text-[#999]'
                        }`}
                      >
                        <FaComments size={12} className="text-[#999]" />
                        <span>Default</span>
                      </button>
                      <button
                        onClick={() => {
                          setSelectedChatType('announcement');
                          setShowChatTypeDropdown(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-[#2a2a2a] transition-colors ${
                          selectedChatType === 'announcement' ? 'bg-yellow-900/30 text-yellow-400' : 'text-[#999]'
                        }`}
                      >
                        <FaBullhorn size={12} className="text-yellow-400" />
                        <span className="text-yellow-400">Announcement</span>
                      </button>
                      <button
                        onClick={() => {
                          setSelectedChatType('system');
                          setShowChatTypeDropdown(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-[#2a2a2a] transition-colors ${
                          selectedChatType === 'system' ? 'bg-red-900/30 text-red-400' : 'text-[#999]'
                        }`}
                      >
                        <FaExclamationTriangle size={12} className="text-red-400" />
                        <span className="text-red-400">System</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
              
              <input
                ref={inputRef}
                type="text"
                value={inputMessage}
                onChange={handleInputChange}
                onPointerDown={(event) => {
                  event.stopPropagation();
                  releaseUnityCapture();
                }}
                onKeyDown={(event) => handleChatInputKeyDown(event, handleSendMessage)}
                onKeyUp={(event) => event.stopPropagation()}
                onFocus={() => {
                  if (inputBlurTimeoutRef.current) {
                    clearTimeout(inputBlurTimeoutRef.current);
                    inputBlurTimeoutRef.current = null;
                  }
                  // Sync release — useOverlay runs after layout; Unity would otherwise keep Cmd/Ctrl+C for the game.
                  releaseUnityCapture();
                  setIsInputFocused(true);
                }}
                onBlur={() => {
                  if (inputBlurTimeoutRef.current) {
                    clearTimeout(inputBlurTimeoutRef.current);
                  }
                  // Defer: opening the context menu blurs the input; if we drop the overlay immediately,
                  // Unity re-captures the keyboard before the browser runs "Copy" from the menu.
                  inputBlurTimeoutRef.current = setTimeout(() => {
                    inputBlurTimeoutRef.current = null;
                    if (document.activeElement !== inputRef.current) setIsInputFocused(false);
                  }, 400);
                }}
                placeholder={
                  !isConnected 
                    ? "Connecting… you can type while you wait"
                    : selectedChatType === 'announcement' && localIsIntersectStaff
                    ? "Type an announcement..."
                    : selectedChatType === 'system' && localIsIntersectStaff
                    ? "Type a system message..."
                    : "Type a message..."
                }
                className={`flex-1 px-3 py-2 bg-[#1a1a1a] border rounded-md text-sm placeholder-[#666] focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${
                  selectedChatType === 'announcement' && localIsIntersectStaff
                    ? 'border-yellow-500/50 text-yellow-400 focus:border-yellow-500'
                    : selectedChatType === 'system' && localIsIntersectStaff
                    ? 'border-red-500/50 text-red-400 focus:border-red-500'
                    : 'border-white/15 text-white focus:border-blue-500'
                }`}
              />
              <button
                onClick={handleSendMessage}
                disabled={!isConnected || !inputMessage.trim()}
                className={`px-4 py-2 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${
                  selectedChatType === 'announcement' && localIsIntersectStaff
                    ? 'bg-yellow-600 hover:bg-yellow-700'
                    : selectedChatType === 'system' && localIsIntersectStaff
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
                title="Send message"
              >
                <FaPaperPlane size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmMessageId && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10002]"
          style={{ pointerEvents: 'auto' }}
          onClick={() => !isDeletingMessage && setDeleteConfirmMessageId(null)}
        >
          <div 
            className="unity-ivs-glass-modal-card p-6 max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <img 
                src={intersectStaffIcon} 
                alt="Mod Action" 
                className="w-5 h-5"
              />
              Moderator Action: Delete Message
            </h3>
            <p className="text-gray-300 mb-6">
              As a moderator, you are about to delete this message. This action cannot be undone and will remove the message for all users in the room.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirmMessageId(null)}
                disabled={isDeletingMessage}
                className="px-4 py-2 bg-[#2a2a2a] text-white rounded-md hover:bg-[#333] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteMessage(deleteConfirmMessageId)}
                disabled={isDeletingMessage}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeletingMessage ? (
                  <>
                    <FaSpinner size={14} className="animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <FaTrash size={14} />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Teleport User Confirmation Modal */}
      {teleportConfirmMessage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10002]"
          style={{ pointerEvents: 'auto' }}
          onClick={handleCancelTeleport}
        >
          <div 
            className="unity-ivs-glass-modal-card p-6 max-w-md mx-4 ring-2 ring-blue-500/35"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <img 
                src={intersectStaffIcon} 
                alt="Mod Action" 
                className="w-5 h-5"
              />
              Moderator Action: Teleport User
            </h3>
            <div className="mb-6 space-y-3">
              <p className="text-gray-300">
                Send teleport command to <span className="text-blue-400 font-semibold">{teleportConfirmMessage.username}</span>?
              </p>
              <p className="text-gray-400 text-sm">
                They will be teleported to the podium on their screen.
              </p>
              <div className="bg-[#2a2a2a] rounded-lg p-3">
                <div className="text-xs text-[#999] mb-1">Podium Position:</div>
                <div className="text-sm text-white font-mono">
                  {podiumPosition}
                </div>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelTeleport}
                className="px-4 py-2 bg-[#2a2a2a] text-white rounded-md hover:bg-[#333] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmTeleport}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <FaMapMarkerAlt size={14} />
                Teleport
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Grant Screen Share Permission Confirmation Modal */}
      {screenShareConfirmMessage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10002]"
          style={{ pointerEvents: 'auto' }}
          onClick={handleCancelGrantScreenShare}
        >
          <div 
            className="unity-ivs-glass-modal-card p-6 max-w-md mx-4 ring-2 ring-green-500/35"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <img 
                src={intersectStaffIcon} 
                alt="Mod Action" 
                className="w-5 h-5"
              />
              Moderator Action: Grant Screen Share
            </h3>
            <div className="mb-6 space-y-3">
              <p className="text-gray-300">
                Grant screen share permission to <span className="text-green-400 font-semibold">{screenShareConfirmMessage.username}</span>?
              </p>
              <p className="text-gray-400 text-sm">
                They will be able to share their screen to the stage.
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelGrantScreenShare}
                className="px-4 py-2 bg-[#2a2a2a] text-white rounded-md hover:bg-[#333] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmGrantScreenShare}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <FaDesktop size={14} />
                Grant Permission
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke Screen Share Permission Confirmation Modal */}
      {screenShareRevokeConfirmMessage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10002]"
          style={{ pointerEvents: 'auto' }}
          onClick={handleCancelRevokeScreenShare}
        >
          <div 
            className="unity-ivs-glass-modal-card p-6 max-w-md mx-4 ring-2 ring-red-500/35"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <img 
                src={intersectStaffIcon} 
                alt="Mod Action" 
                className="w-5 h-5"
              />
              Moderator Action: Revoke Screen Share
            </h3>
            <div className="mb-6 space-y-3">
              <p className="text-gray-300">
                Revoke screen share permission from <span className="text-red-400 font-semibold">{screenShareRevokeConfirmMessage.username}</span>?
              </p>
              <p className="text-gray-400 text-sm">
                They will no longer be able to share their screen.
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelRevokeScreenShare}
                className="px-4 py-2 bg-[#2a2a2a] text-white rounded-md hover:bg-[#333] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRevokeScreenShare}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <FaDesktop size={14} />
                Revoke Permission
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

IVSRealtimeChat.displayName = 'IVSRealtimeChat';

export default IVSRealtimeChat;
